import {
  companyIsSoleTrader,
  marketHasCheckpointStep,
} from '~/constants/markets';
import {
  ApplicationInfoLevel,
  ApplicationStatus,
  ClientType,
  CountryCode,
  FlowTypes,
  GlobalErrorType,
  StepStatus,
} from '~/enums';
import { logToSentry } from '~/helpers/loggers.helper';
import {
  ILoanApplicationClient,
  ILoanApplicationGuarantor,
} from '~/interfaces/loanApplication';
import { CompanyTypeCategory } from '~/interfaces/shared';
import { loanApplicationDTOMappers } from '~/mappers/DTO/loanApplication';
import { loanApplicationDTOSchemas } from '~/schemas/DTO/loanApplication';
import {
  apiGetClientCompanies,
  apiGetGuarantorsAndBoardMembers,
  apiGetGuarantorsAndBoardMembersV2,
  apiGetLoanApplication,
  apiPatchLoanApplication,
  apiPostLoanApplication,
} from '~/services/api';
import { ApiStatus } from '~/store/types/sharedTypes';
import { AppDispatch, GetStateFunc } from '..';
import { onboardingApiUtil } from '../apis/endpoints/onboardingApi/onboarding.api';
import {
  setCustomGlobalError,
  toggleGlobalErrorOn,
} from '../slices/globalError.slice';
import {
  clearLoanApplicationState,
  goToNextStep,
  setApiStatus,
  setClientId,
  setCompanyIsNew,
  setDataPointsShared,
  setInfoLevel,
  setIsCompanyTypeCategoryRequested,
  updateApplicationStatus,
  updateCurrentOrganizationIdentifier,
  updateCurrentStepNumber,
  updateForm,
  updateOverview,
} from '../slices/loanApplication.slice';
import { setUser } from '../slices/user.slice';
import { pushToGtmLoanAction } from './gtmActions';

export const getLoanApplication = (
  uuid: string,
  currentStepFinished?: boolean
) => async (dispatch: AppDispatch, getState: GetStateFunc) => {
  dispatch(setApiStatus({ application: ApiStatus.Started }));
  const {
    intl,
    loanApplication: { form: currentForm, flowType },
  } = getState();
  const sendPublicToken =
    flowType === FlowTypes.Manual && currentForm.applicantAuthenticated;
  try {
    const clientApplicationData = await apiGetLoanApplication(
      currentForm.applicantAuthenticated,
      uuid,
      intl.market,
      sendPublicToken
    );

    if (clientApplicationData?.indicativeOffer) {
      const parsedIndicativeOffer = loanApplicationDTOSchemas.getIndicativeOfferRes.safeParse(
        clientApplicationData.indicativeOffer
      );
      if (parsedIndicativeOffer.success) {
        dispatch(
          onboardingApiUtil.upsertQueryData(
            'getIndicativeOffer',
            { applicationUuid: uuid },
            parsedIndicativeOffer.data
          )
        );
      }
    }

    if (currentStepFinished) {
      dispatch(pushToGtmLoanAction({ stepStatus: StepStatus.Finished }));
    }

    if (clientApplicationData && clientApplicationData.finalized === false) {
      const mappedFormData = loanApplicationDTOMappers.applicationFromDTO(
        clientApplicationData,
        currentForm
      );

      dispatch(
        updateCurrentStepNumber(clientApplicationData.completedStep + 1)
      );

      dispatch(
        setIsCompanyTypeCategoryRequested(
          !!clientApplicationData.isCompanyTypeCategoryRequested
        )
      );
      dispatch(setInfoLevel(clientApplicationData.infoLevel));
      dispatch(updateForm(mappedFormData));
      dispatch(setApiStatus({ application: ApiStatus.Completed }));
      dispatch(
        updateCurrentOrganizationIdentifier(
          mappedFormData.organizationNumber ||
            mappedFormData.creditReportProvider?.creditReportProviderNumber
        )
      );
      if (clientApplicationData.clientId) {
        dispatch(setClientId(clientApplicationData.clientId));
      }
      dispatch(setDataPointsShared(clientApplicationData.dataPointsShared));
    } else {
      // TODO: decide what to do if no application was found OR if application is already finalized
      dispatch(clearLoanApplicationState());
    }
  } catch (err) {
    logToSentry(err, 'apiGetLoanApplication', {
      applicationUuid: uuid,
    });
    dispatch(setApiStatus({ application: ApiStatus.Failed }));
    if (
      err.message === 'No ACCESS_TOKEN found!' ||
      err?.response?.status === 401
    ) {
      dispatch(toggleGlobalErrorOn(GlobalErrorType.TOKEN_EXPIRED));
    } else {
      dispatch(clearLoanApplicationState());
      dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
    }
  }
};

export const getClientCompanies = () => async (
  dispatch: AppDispatch,
  getState: GetStateFunc
) => {
  dispatch(setApiStatus({ clients: ApiStatus.Started }));
  const { intl, loanApplication } = getState();
  if (!loanApplication.userId) return;
  try {
    const clientCompaniesData = await apiGetClientCompanies(
      loanApplication.userId,
      intl.market
    );
    const validClients = clientCompaniesData?.clients?.filter((client) =>
      intl.market === CountryCode.DE
        ? client.credit_report_provider_number
        : client.company_no
    );

    if (validClients?.length) {
      dispatch(
        updateForm({
          clientType: validClients.some((client: ILoanApplicationClient) => {
            let companyMatch = false;

            if (client.company_no && loanApplication.form.organizationNumber) {
              companyMatch =
                client.company_no === loanApplication.form.organizationNumber;
            }

            if (
              client.credit_report_provider_number &&
              loanApplication.form.creditReportProvider
                ?.creditReportProviderNumber
            ) {
              companyMatch =
                client.credit_report_provider_number ===
                loanApplication.form.creditReportProvider
                  ?.creditReportProviderNumber;
            }
            return companyMatch && client.active;
          })
            ? ClientType.RLC
            : ClientType.NLC,
        })
      );
      if (loanApplication.flowType === FlowTypes.Authenticated) {
        dispatch(updateOverview({ clients: validClients }));
      }
    } else {
      dispatch(setCompanyIsNew(true));
    }
    dispatch(setApiStatus({ clients: ApiStatus.Completed }));
  } catch (err) {
    logToSentry(err, 'apiGetClientCompanies', {
      clientId: loanApplication.userId,
    });
    dispatch(setApiStatus({ clients: ApiStatus.Failed }));
  }
};

export const getGuarantorsAndBoardMembersSvcOnboarding = () => async (
  dispatch: AppDispatch,
  getState: GetStateFunc
) => {
  const { market } = getState().intl;
  const {
    organizationNumber,
    applicantAuthenticated,
    guarantors: applicationGuarantors,
    applicationUuid,
  } = getState().loanApplication.form;

  if (!applicationUuid) return;

  dispatch(setApiStatus({ guarantors: ApiStatus.Started }));

  try {
    const guarantorAndBoardMembers = await apiGetGuarantorsAndBoardMembersV2({
      applicationUuid,
      countryCode: market,
      applicantAuthenticated,
    });

    if (guarantorAndBoardMembers.data) {
      let guarantors = guarantorAndBoardMembers.data.map(
        (apiGuarantor): ILoanApplicationGuarantor => {
          const commonProperties = {
            isChecked: !!apiGuarantor.latestGuarantor,
          };

          if (apiGuarantor.address?.postCode) {
            const { postCode, street, city, houseNo } = apiGuarantor.address;

            return {
              ...apiGuarantor,
              addressFields: {
                zipCode: postCode,
                streetName: street || '',
                city: city || '',
                houseNumber: houseNo || '',
              },
              ...commonProperties,
            };
          }

          return { ...apiGuarantor, ...commonProperties };
        }
      );

      // filter out the guarantors from API that also exists in application guarantors list
      if (applicationGuarantors?.length) {
        guarantors = guarantors.filter(
          (apiGuarantor: ILoanApplicationGuarantor) =>
            !applicationGuarantors.some(
              (applicationGuarantor) =>
                applicationGuarantor.guarantorId === apiGuarantor.guarantorId
            )
        );
        guarantors.push(...applicationGuarantors);
      }
      const sortedGuarantor = guarantors.sort(
        (guarantor: ILoanApplicationGuarantor) =>
          guarantor.latestGuarantor ? -1 : 1
      );
      dispatch(
        updateForm({
          guarantors: sortedGuarantor,
        })
      );
    }
    dispatch(setApiStatus({ guarantors: ApiStatus.Completed }));
  } catch (err) {
    logToSentry(err, 'apiGetGuarantorsAndBoardMembers', {
      organizationNumber,
    });
    dispatch(setApiStatus({ guarantors: ApiStatus.Failed }));
  }
};

/**
 * API for fetching board members, and existing guarantors from current loan in case of RLC.
 * Retries are needed because backend might not be done with fetching board members
 * from the credit report.
 * Retries are not needed for sole traders though, because they dont have board members.
 */
export const getGuarantorsAndBoardMembers = () => async (
  dispatch: AppDispatch,
  getState: GetStateFunc
) => {
  const { market } = getState().intl;
  const {
    applicantAuthenticated,
    companyType,
    guarantors: applicationGuarantors,
    applicationUuid,
  } = getState().loanApplication.form;

  if (!applicationUuid) return;

  dispatch(setApiStatus({ guarantors: ApiStatus.Started }));
  const withRetry = !companyIsSoleTrader(market, companyType);
  try {
    const guarantorAndBoardMembers = await apiGetGuarantorsAndBoardMembers(
      applicationUuid,
      market,
      applicantAuthenticated,
      withRetry
    );

    if (guarantorAndBoardMembers.maxRetriesReached) {
      logToSentry(
        new Error('Max api call retries reached'),
        'apiGetGuarantorsAndBoardMembers'
      );
    }

    if (guarantorAndBoardMembers.data) {
      let guarantors = guarantorAndBoardMembers.data.map(
        (apiGuarantor): ILoanApplicationGuarantor => {
          const commonProperties = {
            isChecked: !!apiGuarantor.latestGuarantor,
          };

          if (apiGuarantor.address?.postCode) {
            const { postCode, street, city, houseNo } = apiGuarantor.address;

            return {
              ...apiGuarantor,
              addressFields: {
                zipCode: postCode,
                streetName: street || '',
                city: city || '',
                houseNumber: houseNo || '',
              },
              ...commonProperties,
            };
          }

          return { ...apiGuarantor, ...commonProperties };
        }
      );

      // filter out the guarantors from API that also exists in application guarantors list
      if (applicationGuarantors?.length) {
        guarantors = guarantors.filter(
          (apiGuarantor: ILoanApplicationGuarantor) =>
            !applicationGuarantors.some(
              (applicationGuarantor) =>
                applicationGuarantor.guarantorId === apiGuarantor.guarantorId
            )
        );
        guarantors.push(...applicationGuarantors);
      }
      const sortedGuarantor = guarantors.sort(
        (guarantor: ILoanApplicationGuarantor) =>
          guarantor.latestGuarantor ? -1 : 1
      );
      dispatch(
        updateForm({
          guarantors: sortedGuarantor,
        })
      );
    }
    dispatch(setApiStatus({ guarantors: ApiStatus.Completed }));
  } catch (err) {
    logToSentry(err, 'apiGetGuarantorsAndBoardMembers', {
      applicationUuid,
    });
    dispatch(setApiStatus({ guarantors: ApiStatus.Failed }));
  }
};

export const postApplication = () => async (
  dispatch: AppDispatch,
  getState: GetStateFunc
) => {
  const { intl } = getState();
  const storedLandingUrl = sessionStorage.getItem('landingUrl') || '';
  const landingUrl = new URL(storedLandingUrl);
  landingUrl.searchParams.set('languageCode', intl.language);
  const referenceUrl = sessionStorage.getItem('referrerUrl') || '';
  dispatch(
    updateForm({
      guarantors: [],
      bankIntegration: false,
      bankConnected: false,
      applicationUuid: '',
      landingUrl: landingUrl.href,
      referenceUrl,
      eskatConnected: false,
      qCode: '',
    })
  );

  const {
    loanApplication: { form, dataPointsShared },
    user: { isAuthenticated },
  } = getState();

  const shouldSendTurnoverSharedEvent =
    form.turnover &&
    form.incomeReceivedInForeignCurrencies !== undefined &&
    !dataPointsShared.turnover;

  if (shouldSendTurnoverSharedEvent) {
    dispatch(setDataPointsShared({ turnover: true }));
  }

  const shouldSendLoanPurposeSharedEvent =
    form.purposeOfLoan && !dataPointsShared.loanPurpose;
  if (shouldSendLoanPurposeSharedEvent) {
    dispatch(setDataPointsShared({ loanPurpose: true }));
  }

  const {
    loanApplication: {
      currentStepNumber,
      infoLevel,
      dataPointsShared: updatedDataPointsShared,
    },
  } = getState();

  const requestData = loanApplicationDTOMappers.applicationToDTO({
    form,
    currentStepNumber,
    market: intl.market,
    shouldPostApplication: true,
    finalized: false,
    currentStepCompleted: true,
    dataPointsShared: updatedDataPointsShared,
  });

  dispatch(setApiStatus({ post: ApiStatus.Started }));
  try {
    const result = await apiPostLoanApplication(requestData);

    dispatch(pushToGtmLoanAction({ stepStatus: StepStatus.Finished }));

    dispatch(
      updateCurrentOrganizationIdentifier(
        requestData.organization.organizationNumber ||
          requestData.organization.creditReportProvider
            ?.creditReportProviderNumber
      )
    );
    dispatch(updateForm({ applicationUuid: result.applicationUuid }));

    if (result.applicationId) {
      dispatch(updateForm({ applicationId: result.applicationId }));
    }
    if (result.qCode) {
      dispatch(updateForm({ qCode: result.qCode }));
    }
    if (result.clientId) {
      dispatch(setClientId(result.clientId));
    }
    if (result.userCode) {
      dispatch(updateForm({ userCode: result.userCode }));
    }
    if (result.accessToken) {
      document.cookie = `publicToken=${result.accessToken}; Max-Age=36000`;
    }

    const shouldSendBasicApplicationEvent =
      infoLevel === ApplicationInfoLevel.PRE_BASIC &&
      result.infoLevel === ApplicationInfoLevel.BASIC;

    if (shouldSendBasicApplicationEvent) {
      dispatch(pushToGtmLoanAction({ actionName: 'basic_application_sent' }));
    }

    shouldSendTurnoverSharedEvent &&
      dispatch(
        pushToGtmLoanAction({ actionName: 'turnover_data_point_shared' })
      );

    shouldSendLoanPurposeSharedEvent &&
      dispatch(
        pushToGtmLoanAction({ actionName: 'loan_purpose_data_point_shared' })
      );

    dispatch(setInfoLevel(result.infoLevel));

    if (result.accessToken) {
      document.cookie = `publicToken=${result.accessToken}; Max-Age=36000`;
      dispatch(setUser({ isAuthenticated, authToken: result.accessToken }));
    }

    dispatch(goToNextStep());

    dispatch(
      setApiStatus({ post: ApiStatus.Completed, guarantors: ApiStatus.Idle })
    );
  } catch (err) {
    dispatch(pushToGtmLoanAction({ stepStatus: StepStatus.Error }));
    logToSentry(err, 'apiPostLoanApplication');
    dispatch(setApiStatus({ post: ApiStatus.Failed }));
    dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
    throw err;
  }
};
interface PatchApplicationParams {
  finalized?: boolean;
  currentStepCompleted?: boolean;
  choseToSubmitBasicApplication?: boolean;
}

export const patchApplication = ({
  finalized = false,
  currentStepCompleted = true,
  choseToSubmitBasicApplication = false,
}: PatchApplicationParams = {}) => async (
  dispatch: AppDispatch,
  getState: GetStateFunc
) => {
  const {
    loanApplication: {
      form,
      currentStepNumber,
      nextStepName,
      isCompanyTypeCategoryRequested,
      clientId,
      flowType,
      infoLevel,
      dataPointsShared,
    },
    intl,
  } = getState();

  // nextStepName will be null when the current step is the last basic step
  const shouldGetCompanyTypeCategory =
    nextStepName === null || nextStepName === 'guarantor_selector_step';
  const getCompanyTypeCategory =
    !isCompanyTypeCategoryRequested && shouldGetCompanyTypeCategory;

  const shouldSendTurnoverSharedEvent =
    form.turnover &&
    form.incomeReceivedInForeignCurrencies !== undefined &&
    !dataPointsShared.turnover;

  if (shouldSendTurnoverSharedEvent) {
    dispatch(setDataPointsShared({ turnover: true }));
  }

  const shouldSendLoanPurposeSharedEvent =
    form.purposeOfLoan && !dataPointsShared.loanPurpose;
  if (shouldSendLoanPurposeSharedEvent) {
    dispatch(setDataPointsShared({ loanPurpose: true }));
  }

  const shouldSendWantsCardSharedEvent =
    typeof form.cardOptIn === 'boolean' && !dataPointsShared.cardOptIn;
  if (shouldSendWantsCardSharedEvent) {
    dispatch(setDataPointsShared({ cardOptIn: true }));
  }

  const {
    dataPointsShared: updatedDataPointsShared,
  } = getState().loanApplication;

  const requestData = loanApplicationDTOMappers.applicationToDTO({
    form: {
      ...form,
      choseToSubmitBasicApplication:
        choseToSubmitBasicApplication || form.choseToSubmitBasicApplication,
    },
    currentStepNumber,
    market: intl.market,
    shouldPostApplication: false,
    finalized,
    currentStepCompleted,
    clientId,
    isCompanyTypeCategoryRequested:
      isCompanyTypeCategoryRequested || getCompanyTypeCategory,
    dataPointsShared: updatedDataPointsShared,
  });

  dispatch(setApiStatus({ patch: ApiStatus.Started }));
  if (finalized) {
    dispatch(updateApplicationStatus(ApplicationStatus.ApplicationFinished));
  }
  try {
    const sendPublicToken =
      flowType === FlowTypes.Manual && form.applicantAuthenticated;
    const patchApplicationData = await apiPatchLoanApplication(
      requestData,
      sendPublicToken,
      getCompanyTypeCategory
    );

    if (choseToSubmitBasicApplication) {
      dispatch(updateForm({ choseToSubmitBasicApplication }));
    }

    if (currentStepCompleted) {
      dispatch(pushToGtmLoanAction({ stepStatus: StepStatus.Finished }));
    }

    if (getCompanyTypeCategory) {
      dispatch(setIsCompanyTypeCategoryRequested(true));
    }

    if (patchApplicationData.companyTypeCategory) {
      dispatch(
        updateForm({
          companyTypeCategory: patchApplicationData.companyTypeCategory as CompanyTypeCategory,
        })
      );
    }

    if (patchApplicationData.userCode) {
      dispatch(updateForm({ userCode: patchApplicationData.userCode }));
    }

    const preBasicApplicationTurnedIntoBasic =
      infoLevel === ApplicationInfoLevel.PRE_BASIC &&
      patchApplicationData.infoLevel === ApplicationInfoLevel.BASIC;

    if (
      preBasicApplicationTurnedIntoBasic &&
      !marketHasCheckpointStep[intl.market]
    ) {
      dispatch(pushToGtmLoanAction({ actionName: 'basic_application_sent' }));
    }

    if (shouldSendTurnoverSharedEvent) {
      dispatch(
        pushToGtmLoanAction({ actionName: 'turnover_data_point_shared' })
      );
    }

    if (shouldSendLoanPurposeSharedEvent) {
      dispatch(
        pushToGtmLoanAction({ actionName: 'loan_purpose_data_point_shared' })
      );
    }

    if (shouldSendWantsCardSharedEvent) {
      dispatch(
        pushToGtmLoanAction({ actionName: 'wants_card_data_point_shared' })
      );
    }

    dispatch(setInfoLevel(patchApplicationData.infoLevel));

    if (finalized) {
      dispatch(pushToGtmLoanAction({ stepStatus: StepStatus.Submitted }));
    }

    // determine next step
    if (currentStepCompleted) {
      if (choseToSubmitBasicApplication) {
        dispatch(pushToGtmLoanAction({ actionName: 'basic_application_sent' }));
        dispatch(updateCurrentStepNumber(1));
        await dispatch(patchApplication({ currentStepCompleted: false }));
      } else {
        dispatch(goToNextStep());
      }
    }

    dispatch(setApiStatus({ patch: ApiStatus.Completed }));
  } catch (err) {
    dispatch(
      pushToGtmLoanAction({
        stepStatus: StepStatus.Error,
        errorMessage: err?.response?.data?.message,
        extraContext: {
          context: 'patchApplication',
        },
      })
    );
    logToSentry(err, 'apiPatchLoanApplication');
    if (
      finalized &&
      err?.response?.data?.message === 'Endpoint request timed out'
    ) {
      // If endpoint times out, we will assume this request updated the application in BO but we didn't get a response in time.
      dispatch(setApiStatus({ patch: ApiStatus.Completed }));
    } else {
      if (
        err.message === 'No ACCESS_TOKEN found!' ||
        err?.response?.status === 401
      ) {
        dispatch(setApiStatus({ patch: ApiStatus.Failed }));
        dispatch(toggleGlobalErrorOn(GlobalErrorType.TOKEN_EXPIRED));
      } else if (
        err?.response?.data?.errorCode === 'DuplicateGuarantorsDetected'
      ) {
        dispatch(setApiStatus({ patch: ApiStatus.Failed }));
        dispatch(toggleGlobalErrorOn(GlobalErrorType.CUSTOM_ERROR));
        dispatch(
          setCustomGlobalError({
            title: 'Onboarding.DuplicateGuarantorsDetectedTitle',
            description: 'Onboarding.DuplicateGuarantorsDetectedDescription',
            buttonText: 'Onboarding.DuplicateGuarantorsDetectedButtonText',
          })
        );
      } else {
        dispatch(updateApplicationStatus(ApplicationStatus.None));
        dispatch(setApiStatus({ patch: ApiStatus.Failed }));
        dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
      }
      throw err;
    }
  }
};
